import React, { useState, useEffect } from 'react'
import { CCard, CCardBody, CCardHeader, CAlert, CSpinner, CBadge, CRow, CCol } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBriefcase, cilCalendar, cilLocationPin, cilInfo } from '@coreui/icons'
import { useTranslation } from 'react-i18next'
import { get } from '../utils/apiClient'
import { getContractTypeLabel } from '../utils/contractTypes'
import { getJobFamilyLabel } from '../utils/jobFamilies'
import { getOrganizationalLevelLabel } from '../utils/orgLevels'
import { getSectorLabel } from '../utils/sectors'
import './ExperienceManager.css'

const MemberExperienceViewer = ({ memberId }) => {
  // Namespaces requis pour les libellés des badges (company_industry = business:sector.*).
  const { t } = useTranslation(['forms', 'business'])
  const [experiences, setExperiences] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!memberId) return

    const fetchExperiences = async () => {
      setLoading(true)
      try {
        const data = await get(`/members/${memberId}/experiences`)

        // Tri des expériences par date (les plus récentes en premier)
        const sortedExperiences = data.sort((a, b) => {
          const dateA = a.current ? new Date() : new Date(a.endDate)
          const dateB = b.current ? new Date() : new Date(b.endDate)
          return dateB - dateA
        })

        setExperiences(sortedExperiences)
      } catch (err) {
        console.error('Erreur:', err)
        setError(err.response?.data?.error || err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchExperiences()
  }, [memberId])

  const formatDateRange = (startDate, endDate, current) => {
    const start = new Date(startDate)

    if (isNaN(start.getTime())) return 'Date invalide'

    const options = { year: 'numeric', month: 'short' }
    const startFormatted = start.toLocaleDateString('fr-FR', options)

    if (current) {
      return `${startFormatted} - Aujourd'hui`
    }

    if (endDate) {
      const end = new Date(endDate)
      if (!isNaN(end.getTime())) {
        const endFormatted = end.toLocaleDateString('fr-FR', options)
        return `${startFormatted} - ${endFormatted}`
      }
    }

    return startFormatted
  }

  const getDuration = (startDate, endDate, current) => {
    const start = new Date(startDate)

    if (isNaN(start.getTime())) return ''

    const end = current ? new Date() : endDate ? new Date(endDate) : null

    if (!end || isNaN(end.getTime())) return ''

    const diffTime = Math.abs(end - start)
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30))

    const years = Math.floor(diffMonths / 12)
    const months = diffMonths % 12

    let duration = ''

    if (years > 0) {
      duration += `${years} an${years > 1 ? 's' : ''}`
    }

    if (months > 0 || (months === 0 && years === 0)) {
      if (duration) duration += ' et '
      duration += `${months === 0 && years === 0 ? '1' : months} mois`
    }

    return `(${duration})`
  }

  if (loading) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <div className="text-center py-5">
              <CSpinner color="primary" />
              <p className="mt-3 text-muted">Chargement des expériences professionnelles...</p>
            </div>
          </CCol>
        </CRow>
      </div>
    )
  }

  if (error) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <CAlert color="danger" className="m-3">
              {error}
            </CAlert>
          </CCol>
        </CRow>
      </div>
    )
  }

  if (experiences.length === 0) {
    return (
      <div className="tab-content-container">
        <CRow>
          <CCol>
            <div className="empty-state-container">
              <div className="empty-state-icon">
                <CIcon icon={cilBriefcase} />
              </div>
              <h5>Aucune expérience professionnelle</h5>
              <p className="text-muted">
                Ce membre n'a pas encore partagé d'expérience professionnelle.
              </p>
            </div>
          </CCol>
        </CRow>
      </div>
    )
  }

  return (
    <div className="tab-content-container">
      <CRow>
        <CCol>
          <div className="experience-timeline">
            <div className="experience-count">
              <span>
                {experiences.length} expérience{experiences.length > 1 ? 's' : ''}
              </span>
            </div>
            {experiences.map((exp, index) => (
              <div
                key={exp.id}
                className={`experience-item position-relative mb-4 pb-4 ${index !== experiences.length - 1 ? 'border-bottom' : ''}`}
                style={{ '--index': index }}
              >
                <div className="d-flex">
                  {/* Logo ou icône d'entreprise */}
                  <div className="company-icon me-3">
                    <div className="bg-light rounded p-3 d-flex align-items-center justify-content-center">
                      <CIcon icon={cilBriefcase} size="xl" />
                    </div>
                  </div>

                  {/* Contenu principal */}
                  <div className="experience-content flex-grow-1">
                    <h5 className="mb-1">
                      {exp.title}
                      {exp.current && (
                        <CBadge color="success" className="ms-2" shape="rounded-pill">
                          Actuel
                        </CBadge>
                      )}
                    </h5>

                    <h6 className="mb-2 text-primary">{exp.company}</h6>

                    {/* Badges pro structurés — rien si l'expérience n'en a aucun (fallback propre) */}
                    {(exp.jobFamily || exp.organizationalLevel || exp.contractType || exp.companyIndustry) && (
                      <div className="experience-badges mb-2 d-flex flex-wrap gap-2">
                        {exp.jobFamily && <CBadge color="info">{getJobFamilyLabel(exp.jobFamily, t)}</CBadge>}
                        {exp.organizationalLevel && (
                          <CBadge color="primary">{getOrganizationalLevelLabel(exp.organizationalLevel, t)}</CBadge>
                        )}
                        {exp.contractType && <CBadge color="secondary">{getContractTypeLabel(exp.contractType, t)}</CBadge>}
                        {exp.companyIndustry && (
                          <CBadge color="light" className="text-dark">{getSectorLabel(exp.companyIndustry, t) || exp.companyIndustry}</CBadge>
                        )}
                      </div>
                    )}

                    <div className="experience-details mb-3">
                      <div className="text-muted d-flex align-items-center mb-1">
                        <CIcon icon={cilCalendar} size="sm" className="me-2" />
                        <span>
                          {formatDateRange(exp.startDate, exp.endDate, exp.current)}{' '}
                          <span className="text-secondary">
                            {getDuration(exp.startDate, exp.endDate, exp.current)}
                          </span>
                        </span>
                      </div>

                      {exp.location && (
                        <div className="text-muted d-flex align-items-center">
                          <CIcon icon={cilLocationPin} size="sm" className="me-2" />
                          <span>{exp.location}</span>
                        </div>
                      )}
                    </div>

                    {exp.description && (
                      <div className="experience-description mb-3">
                        <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                          {exp.description}
                        </p>
                      </div>
                    )}

                    {exp.skills && exp.skills.length > 0 && (
                      <div className="experience-skills">
                        {exp.skills.map((skill, skillIndex) => (
                          <CBadge key={skillIndex} color="light" className="me-2 mb-2 text-dark">
                            {skill}
                          </CBadge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CCol>
      </CRow>
    </div>
  )
}

export default MemberExperienceViewer
